// Hideo - the app themes ("looks"): Hideo's own Default plus 20 themes, in two tabs (Dark, Light) and four sections
// (Colours, Special effects, High contrast, Own colours). Without UI: the list, what each theme puts on <html>, its
// colour tokens, the stored choice and the old keys (docs/architecture.md, "Themes"). Applied by the theme store
// (src/stores/theme.ts); background in src/features/sky.ts, sounds in src/features/sounds.ts, picker StylePicker.vue,
// family rules in src/styles/themes.css.
//
// Default = Hideo exactly as it always was: light / dark / automatic and the main colour (creatorOS_theme,
// creatorOS_accent), nothing on <html> but the "dark" class. Any other theme sets <html data-style="key">, the class
// "themed" and its family classes, and decides light or dark itself (it is in one tab; "Follow my device" shows its twin
// in the other tab when the system asks for it). Every theme except the four of "Own colours" follows the main colour
// (adaptive): its palette is built from it (buildPalette) and the theme store writes its tokens on <html>. The four
// fixed themes (Frutiger Aero, Aero night, Four Keys, Four Keys night) keep their own palette.
// Hideo's design tokens (--bg, --card, --g100…, --brand…: tailwind.config.ts) of every theme, built with its reference
// colour, are also in src/styles/theme-tokens.css (written by styleTokenCss(); tests/appThemes.test.ts checks it is up to
// date): the anti-flash script in index.html's <head> falls back on them before the store has run once.
// Stored on this device: creatorOS_style = a key of STYLES or an old key of LEGACY_STYLES (missing or unknown =
// 'default'). Stored keys never change: the old ones are read as their new theme for good.
import { accentVars, hex2rgb, mixc, toHex, type Rgb } from './theme'
import { contrastOf } from '../utils/color'

export const STYLE_KEY = 'creatorOS_style'
export const DEFAULT_STYLE = 'default'

/** The tab of the picker the theme is in */
export type StyleGroup = 'dark' | 'light'
/** Its section: plain colours, special effects, high contrast, or a theme with its own colours (fixed) */
export type StyleKind = 'colors' | 'effects' | 'contrast' | 'fixed'
/** Families of rules in src/styles/themes.css (classes theme-<family> on <html>) */
export type StyleFamily = 'bubbles' | 'fluent' | 'aero' | 'nostalgia' | 'fourkeys' | 'contrast'
/** How an adaptive theme's palette is built from the main colour (buildPalette) */
export type Recipe = 'neutral' | 'deep' | 'bubbles' | 'fluent' | 'nostalgia' | 'contrast' | 'outline' | 'color'

/** A theme's colours (#rrggbb). card / inp may be see-through (cardA, inpA: the background shows behind) */
export interface Palette {
  /** Page background, and the two ends of the still gradient behind the page */
  bg: string; skyA: string; skyB: string
  /** Body text, secondary text, faint text */
  text: string; gray: string; grayDark: string
  /** Main colour and its lighter / hover shade */
  accent: string; accentLight: string
  /** Panels (cards, dialogs, the sidebar), their borders, secondary buttons, fields */
  card: string; cardA?: number; border: string; btn: string; btnBorder: string; inp: string; inpA?: number
  /** Round avatars without a picture */
  sphere: string
  green: string; red: string
}

export interface StyleDef {
  key: string
  /** English name (shown through t(); the Italian is in src/i18n/it/core.ts) */
  name: string
  /** Short description, English (t()) */
  desc: string
  group: StyleGroup
  kind: StyleKind
  /** Light text on dark panels (sets <html class="dark">) */
  dark: boolean
  family: StyleFamily | null
  /** The night version of Nostalgia Channel and Four Keys (class theme-night) */
  night: boolean
  /** Flat panels (class theme-flat): every theme except the two glass Aero ones */
  flat: boolean
  /** Background: 'still' gradient, or the animated scene of the special themes */
  sky: 'still' | 'halos' | 'aurora' | 'lines' | 'glow'
  /** Background music while the sounds are on: its own tune, or the soft chords every theme has */
  music: 'nostalgia' | 'fourkeys' | 'chords'
  /** Follows the main colour (false only for the four themes with their own colours) */
  adaptive: boolean
  /** The same theme in the other tab ("Follow my device" switches between the two) */
  twin: string
  /** Brings its own background (the background patterns of Settings do not apply) */
  ownSky: boolean
  /** Glass panels (src/features/backdrop.ts): always on (Blurred bubbles, Aero: their own rule), always off (Nostalgia, Four
   *  Keys, High contrast: see-through panels would weaken them), or the user's choice */
  glass: 'on' | 'off' | 'user'
  /** Adaptive themes: how the palette is built from the main colour */
  recipe: Recipe | null
  /** The palette: fixed themes' own; adaptive themes' built with their reference colour (the old theme's) */
  palette: Palette
}

// ===================================================================== palettes
const rgb = hex2rgb
const hex = toHex
/** a mixed with b by k (#rrggbb) */
const mix = (a: string, b: string, k: number): string => hex(mixc(rgb(a), rgb(b), k))
/** Colour c moved towards `to` just enough to reach contrast min on every background (fully `to` if never) */
function ensure(c: string, bgs: string[], min: number, to: string): string {
  for (let k = 0; k <= 1.001; k += 0.05) { const x = mix(c, to, k); if (bgs.every(b => contrastOf(rgb(x), rgb(b)) >= min)) return x }
  return to
}
/** The panel as it looks on the page (see-through panels over the page background) */
const seen = (p: Palette, k: 'card' | 'inp'): string => mix(p.bg, p[k], (k === 'card' ? p.cardA : p.inpA) ?? 1)

// Neutral surfaces of the plain themes (the user's ProCut themes, mapped onto Hideo's tokens), without their colour
const ANTHRACITE = { bg: '#1b1c20', skyA: '#1a1b1f', text: '#ececee', gray: '#a2a4ab', grayDark: '#74767d', card: '#232429', border: '#303138', btn: '#2b2c32',
  btnBorder: '#393a42', inp: '#1a1b1f', sphere: '#2e2f35', green: '#6fcf97', red: '#e07a7a' }
const PEARL = { bg: '#f4f4f5', skyA: '#f7f7f8', text: '#1d1e22', gray: '#5c5f68', grayDark: '#8a8d95', card: '#ffffff', border: '#e2e2e6', btn: '#f4f4f6',
  btnBorder: '#d5d6db', inp: '#fafafb', sphere: '#e3e3e7', green: '#2f9e62', red: '#c93a3a' }
const FLUENT_DARK = { bg: '#202020', skyB: '#1f1f1f', text: '#ffffff', gray: '#c5c5c5', grayDark: '#9d9d9d', card: '#2b2b2b', border: '#3a3a3a', btn: '#2d2d2d',
  btnBorder: '#3a3a3a', inp: '#1f1f1f', sphere: '#333333', green: '#6ccb5f', red: '#ff99a4' }
const FLUENT_LIGHT = { bg: '#f3f3f3', skyB: '#f3f3f3', text: '#1a1a1a', gray: '#5d5d5d', grayDark: '#8a8a8a', card: '#fbfbfb', border: '#e5e5e5', btn: '#fdfdfd',
  btnBorder: '#e5e5e5', inp: '#ffffff', sphere: '#e6e6e6', green: '#0f7b0f', red: '#c42b1c' }
const NOSTALGIA_NIGHT = { bg: '#1c2025', skyA: '#23282e', skyB: '#15181c', text: '#e7ebef', gray: '#9ba4ad', grayDark: '#6f7882', card: '#2a2f35', border: '#3c434b',
  btn: '#30363d', btnBorder: '#454d56', inp: '#22272c', sphere: '#2f353c', green: '#5cc779', red: '#ef7a76' }
const NOSTALGIA_DAY = { bg: '#f4f5f7', skyA: '#fdfdfe', skyB: '#e6e9ed', text: '#3c4046', gray: '#737980', grayDark: '#a0a6ad', card: '#ffffff', border: '#d4d9df',
  btn: '#ffffff', btnBorder: '#cdd3da', inp: '#ffffff', sphere: '#eef1f4', green: '#3aa856', red: '#d9534f' }
type Tinted = Record<'bg' | 'skyA' | 'skyB' | 'text' | 'gray' | 'grayDark' | 'card' | 'border' | 'btn' | 'btnBorder' | 'inp' | 'sphere', [string, number]>
// Deep / Tinted: each surface = a neutral mixed with the main colour by a fixed share. The shares were fitted on the old
// Green, Blue, Violet, Amber (dark) and Sage, Sky, Lavender, Apricot (light): with their colour they come back within a
// few units per channel (tests/appThemes.test.ts)
const DEEP_DARK: Tinted = { bg: ['#111011', 0.055], skyA: ['#0e0e0e', 0.05], skyB: ['#101013', 0.15], text: ['#f4f4f3', 0.07], gray: ['#acabaa', 0.155],
  grayDark: ['#747374', 0.15], card: ['#161516', 0.07], border: ['#232323', 0.105], btn: ['#1b1b1d', 0.09], btnBorder: ['#2d2d2d', 0.125], inp: ['#0e0e0e', 0.05],
  sphere: ['#1d1d1d', 0.12] }
const DEEP_LIGHT: Tinted = { bg: ['#fcfcfc', 0.055], skyA: ['#fcfcfc', 0.04], skyB: ['#fffffd', 0.145], text: ['#131212', 0.095], gray: ['#5c5d5f', 0.175],
  grayDark: ['#949495', 0.17], card: ['#ffffff', 0], border: ['#f4f3f1', 0.14], btn: ['#fdfdfc', 0.06], btnBorder: ['#ebebe9', 0.175], inp: ['#ffffff', 0.025],
  sphere: ['#fbfbf8', 0.18] }
// Blurred bubbles: from the old Turquoise, a deeper version of Deep with see-through panels
const BUBBLES_DARK: Tinted = { bg: ['#010203', 0.06], skyA: ['#000102', 0.045], skyB: ['#020304', 0.1], text: ['#ffffff', 0.1], gray: ['#b4b8b8', 0.25],
  grayDark: ['#7e8282', 0.22], card: ['#020305', 0.17], border: ['#0c0d0f', 0.3], btn: ['#030406', 0.23], btnBorder: ['#0e1012', 0.38], inp: ['#010203', 0.1],
  sphere: ['#05070a', 0.27] }
const BUBBLES_LIGHT: Tinted = { bg: ['#ffffff', 0.07], skyA: ['#ffffff', 0.05], skyB: ['#ffffff', 0.13], text: ['#070a0a', 0.15], gray: ['#5a5f5e', 0.2],
  grayDark: ['#8f9493', 0.2], card: ['#ffffff', 0], border: ['#f0f0f0', 0.18], btn: ['#ffffff', 0.03], btnBorder: ['#ececec', 0.22], inp: ['#ffffff', 0],
  sphere: ['#fafafa', 0.2] }
const tint = (t: Tinted, a: string) => Object.fromEntries(Object.entries(t).map(([k, [n, f]]) => [k, mix(n, a, f)])) as Record<keyof Tinted, string>

/** The palette of an adaptive theme for a main colour (#rrggbb). Body text, grey text and (high contrast) borders are
 *  then pushed just enough to stay readable on the panels, whatever the colour */
export function buildPalette(recipe: Recipe, accent: string, dark: boolean): Palette {
  const a = accent.toLowerCase(), W = '#ffffff', K = '#000000'
  const lighter = (k: number) => mix(a, W, k), darker = (k: number) => mix(a, K, k)
  let p: Palette
  switch (recipe) {
    case 'neutral': {
      const n = dark ? ANTHRACITE : PEARL
      p = { ...n, skyB: mix(n.bg, a, 0.08), accent: a, accentLight: dark ? lighter(0.15) : darker(0.12) }
      break
    }
    case 'deep': case 'bubbles': {
      const t = tint(recipe === 'deep' ? (dark ? DEEP_DARK : DEEP_LIGHT) : (dark ? BUBBLES_DARK : BUBBLES_LIGHT), a)
      p = { ...t, accent: a, accentLight: dark ? lighter(0.15) : darker(0.12), green: dark ? '#6fcf97' : '#2f9e62', red: dark ? '#e07a7a' : '#c4453f',
        ...(recipe === 'bubbles' ? { cardA: 0.72 } : {}) }
      break
    }
    case 'fluent':
      p = dark ? { ...FLUENT_DARK, skyA: mix('#1b1b1d', a, 0.08), accent: a, accentLight: lighter(0.15) }
        : { ...FLUENT_LIGHT, skyA: mix('#f3f3f3', a, 0.04), accent: a, accentLight: lighter(0.1) }
      break
    case 'nostalgia':
      p = { ...(dark ? NOSTALGIA_NIGHT : NOSTALGIA_DAY), accent: a, accentLight: dark ? lighter(0.2) : darker(0.1) }
      break
    default: {
      // High contrast: pure black / white surfaces, strong text and borders, the main colour made readable at 4.5:1
      const color = recipe === 'color'
      p = dark
        ? { bg: color ? mix(K, a, 0.3) : K, skyA: color ? mix(K, a, 0.3) : K, skyB: color ? mix(K, a, 0.18) : '#050505', text: W, gray: '#d4d4d4', grayDark: '#ababab',
          card: color ? K : '#0a0a0a', border: color ? '#737373' : '#6b6b6b', btn: color ? '#121212' : '#161616', btnBorder: '#8c8c8c', inp: K,
          sphere: color ? mix(K, a, 0.35) : '#262626', green: '#5ee38d', red: '#ff7b7b', accent: a, accentLight: a }
        : { bg: color ? mix(W, a, 0.14) : W, skyA: color ? mix(W, a, 0.14) : W, skyB: color ? mix(W, a, 0.24) : '#f5f5f5', text: K, gray: '#2e2e2e', grayDark: '#555555',
          card: W, border: color ? K : '#6b6b6b', btn: color ? mix(W, a, 0.08) : '#f0f0f0', btnBorder: color ? K : '#555555', inp: W,
          sphere: color ? mix(W, a, 0.25) : '#e0e0e0', green: '#157a3c', red: '#b3261e', accent: a, accentLight: a }
      p.accent = ensure(a, [p.bg, p.card, p.inp], 4.5, dark ? W : K)
      p.accentLight = dark ? mix(p.accent, W, 0.2) : mix(p.accent, K, 0.15)
      if (recipe === 'outline') p.border = p.btnBorder = p.accent
    }
  }
  const card = seen(p, 'card'), on = [card, p.bg], to = dark ? W : K
  p.text = ensure(p.text, on, 7, to)
  p.gray = ensure(p.gray, on, recipe === 'contrast' || recipe === 'outline' || recipe === 'color' ? 7 : 4.5, to)
  if (recipe === 'contrast' || recipe === 'outline' || recipe === 'color') p.border = ensure(p.border, [card], 3, to)
  return p
}

// ===================================================================== the list
// Fixed palettes of the four themes with their own colours (from the user's ProCut themes)
const P = (bg: string, skyA: string, skyB: string, text: string, gray: string, grayDark: string, accent: string, accentLight: string,
  card: string, border: string, btn: string, btnBorder: string, inp: string, sphere: string, green: string, red: string, extra: Partial<Palette> = {}): Palette =>
  ({ bg, skyA, skyB, text, gray, grayDark, accent, accentLight, card, border, btn, btnBorder, inp, sphere, green, red, ...extra })

type Def = Pick<StyleDef, 'key' | 'name' | 'desc' | 'group' | 'kind' | 'dark' | 'twin'> & Partial<Pick<StyleDef, 'family' | 'night' | 'sky' | 'music' | 'glass'>>
  & ({ recipe: Recipe; ref: string } | { palette: Palette })
const def = (d: Def): StyleDef => {
  const recipe = 'recipe' in d ? d.recipe : null
  const { family = null, night = false, sky = 'still', music = 'chords' } = d
  return {
    key: d.key, name: d.name, desc: d.desc, group: d.group, kind: d.kind, dark: d.dark, twin: d.twin, family, night, sky, music,
    flat: family !== 'aero', adaptive: !!recipe, recipe, ownSky: sky !== 'still',
    glass: d.glass ?? (family === 'bubbles' || family === 'aero' ? 'on' : family === 'nostalgia' || family === 'fourkeys' || family === 'contrast' ? 'off' : 'user'),
    palette: 'recipe' in d ? buildPalette(d.recipe, d.ref, d.dark) : d.palette
  }
}

/** Every theme except Default, in the picker's order: the dark tab, then the light tab (twins in the same order) */
export const STYLES: readonly StyleDef[] = [
  // ===== Dark =====
  def({ key: 'charcoal', name: 'Anthracite', desc: 'neutral anthracite with your colour', group: 'dark', kind: 'colors', dark: true, twin: 'pearl_ac', recipe: 'neutral', ref: '#d64545' }),
  def({ key: 'deep', name: 'Deep', desc: 'very dark shades of your colour', group: 'dark', kind: 'colors', dark: true, twin: 'deep_light', recipe: 'deep', ref: '#8b5cf6' }),
  def({ key: 'bubbles', name: 'Blurred bubbles', desc: 'soft lights in your colour that sway, behind glass panels', group: 'dark', kind: 'effects', dark: true, twin: 'bubbles_light',
    family: 'bubbles', sky: 'halos', recipe: 'bubbles', ref: '#22c9a0' }),
  def({ key: 'fluent', name: 'Fluent', desc: 'neat corners and soft shadows', group: 'dark', kind: 'effects', dark: true, twin: 'fluent_light', family: 'fluent', recipe: 'fluent', ref: '#60cdff' }),
  def({ key: 'nostalgia_night', name: 'Nostalgia Channel night', desc: 'anthracite, scan lines and bubbles, with its own music', group: 'dark', kind: 'effects', dark: true,
    twin: 'nostalgia', family: 'nostalgia', night: true, sky: 'lines', music: 'nostalgia', recipe: 'nostalgia', ref: '#3cc4f3' }),
  def({ key: 'contrast', name: 'Pure black', desc: 'black page, white text, strong borders', group: 'dark', kind: 'contrast', dark: true, twin: 'contrast_light',
    family: 'contrast', recipe: 'contrast', ref: '#e85d4e' }),
  def({ key: 'contrast_outline', name: 'Bright outlines', desc: 'black page, panels outlined in your colour', group: 'dark', kind: 'contrast', dark: true,
    twin: 'contrast_outline_light', family: 'contrast', recipe: 'outline', ref: '#e85d4e' }),
  def({ key: 'contrast_color', name: 'Full colour', desc: 'a dark page of your colour, black panels', group: 'dark', kind: 'contrast', dark: true,
    twin: 'contrast_color_light', family: 'contrast', recipe: 'color', ref: '#e85d4e' }),
  def({ key: 'aero_night', name: 'Aero night', desc: 'smoked glass and a night aurora', group: 'dark', kind: 'fixed', dark: true, twin: 'aero', family: 'aero', sky: 'aurora',
    palette: P('#020a12', '#020a12', '#0a2a35', '#eef6ff', '#a9c3cc', '#7d97a1', '#19b9d8', '#7fe9ff', '#080e14', '#2b3d47', '#141e28', '#4a6878', '#04090d', '#1b2a3a', '#6dffb8', '#ff8f8f', { cardA: 0.62, inpA: 0.6 }) }),
  def({ key: 'fourkeys_night', name: 'Four Keys night', desc: 'very dark green and four coloured keys', group: 'dark', kind: 'fixed', dark: true, twin: 'fourkeys', family: 'fourkeys',
    night: true, sky: 'glow', music: 'fourkeys',
    palette: P('#07100a', '#0b190f', '#040906', '#eef5ef', '#a1b2a5', '#6c7d70', '#2fb53a', '#55d264', '#0f1c12', '#1f3424', '#142418', '#26412c', '#0a150d', '#15281a', '#55d264', '#f1707a') }),
  // ===== Light =====
  def({ key: 'pearl_ac', name: 'Pearl', desc: 'neutral light grey with your colour', group: 'light', kind: 'colors', dark: false, twin: 'charcoal', recipe: 'neutral', ref: '#c93a3a' }),
  def({ key: 'deep_light', name: 'Tinted', desc: 'pale shades of your colour', group: 'light', kind: 'colors', dark: false, twin: 'deep', recipe: 'deep', ref: '#7c4ddb' }),
  def({ key: 'bubbles_light', name: 'Light blurred bubbles', desc: 'soft lights in your colour that sway, on a light page', group: 'light', kind: 'effects', dark: false,
    twin: 'bubbles', family: 'bubbles', sky: 'halos', recipe: 'bubbles', ref: '#13a582' }),
  def({ key: 'fluent_light', name: 'Fluent light', desc: 'neat corners and soft shadows, light', group: 'light', kind: 'effects', dark: false, twin: 'fluent', family: 'fluent',
    recipe: 'fluent', ref: '#005fb8' }),
  def({ key: 'nostalgia', name: 'Nostalgia Channel', desc: 'white, scan lines and bubbles, with its own music', group: 'light', kind: 'effects', dark: false, twin: 'nostalgia_night',
    family: 'nostalgia', sky: 'lines', music: 'nostalgia', recipe: 'nostalgia', ref: '#2fb8ec' }),
  def({ key: 'contrast_light', name: 'Pure white', desc: 'white page, black text, strong borders', group: 'light', kind: 'contrast', dark: false, twin: 'contrast',
    family: 'contrast', recipe: 'contrast', ref: '#e85d4e' }),
  def({ key: 'contrast_outline_light', name: 'Bright outlines light', desc: 'white page, panels outlined in your colour', group: 'light', kind: 'contrast', dark: false,
    twin: 'contrast_outline', family: 'contrast', recipe: 'outline', ref: '#e85d4e' }),
  def({ key: 'contrast_color_light', name: 'Ink on colour', desc: 'a pale page of your colour, black text and borders', group: 'light', kind: 'contrast', dark: false,
    twin: 'contrast_color', family: 'contrast', recipe: 'color', ref: '#e85d4e' }),
  // Frutiger Aero: bright aqua sky with white text on glass (so it is "dark" for the text colours)
  def({ key: 'aero', name: 'Frutiger Aero', desc: 'glossy glass and a moving aurora', group: 'light', kind: 'fixed', dark: true, twin: 'aero_night', family: 'aero', sky: 'aurora',
    palette: P('#0b6f78', '#0b6f78', '#2aa6c9', '#f2fbff', '#c3e2e8', '#95bcc6', '#1fb7dc', '#8ef0ff', '#15526a', '#4f8ea3', '#1a7896', '#5aa9c8', '#03323d', '#2c3f8f', '#7dffb0', '#ff8f8f', { cardA: 0.6, inpA: 0.62 }) }),
  def({ key: 'fourkeys', name: 'Four Keys', desc: 'white, a green glow and four coloured keys', group: 'light', kind: 'fixed', dark: false, twin: 'fourkeys_night', family: 'fourkeys',
    sky: 'glow', music: 'fourkeys',
    palette: P('#f6f8f6', '#ffffff', '#e8f2e9', '#1b1f1c', '#5c6860', '#8c978f', '#107c10', '#0d6a0d', '#ffffff', '#d8e3d9', '#f3f6f3', '#d0dbd1', '#ffffff', '#e6efe7', '#107c10', '#d13438') })
]

/** Default in the picker (in both tabs: its preview is Hideo's own light or dark with the user's colour) */
export const DEFAULT_NAME = 'Default'
export const DEFAULT_DESC = "Hideo's own look, with your colour"

/** The picker's tabs and sections */
export const STYLE_GROUPS: readonly [StyleGroup, string][] = [['dark', 'Dark'], ['light', 'Light']]
export const STYLE_KINDS: readonly [StyleKind, string][] = [['colors', 'Colours'], ['effects', 'Special effects'], ['contrast', 'High contrast'], ['fixed', 'Own colours']]

const BY_KEY = new Map(STYLES.map(s => [s.key, s]))
/** A theme by key (undefined for Default, old and unknown keys: see parseStyle) */
export const styleDef = (key: string | null | undefined): StyleDef | undefined => (key ? BY_KEY.get(key) : undefined)

/** The themes of the old list (before 4/11/2026), still valid as stored values: their new theme and their own main colour
 *  (shown until the user picks a theme or a colour again; fixed themes and Default kept their key) */
export const LEGACY_STYLES: Readonly<Record<string, { to: string; accent: string }>> = {
  turquoise: { to: 'bubbles', accent: '#22c9a0' }, turquoise_light: { to: 'bubbles_light', accent: '#13a582' },
  anthracite: { to: 'charcoal', accent: '#d64545' }, black: { to: 'charcoal', accent: '#d8d8dc' },
  green: { to: 'deep', accent: '#4caf7d' }, blue: { to: 'deep', accent: '#4a86e0' }, violet: { to: 'deep', accent: '#8b5cf6' }, amber: { to: 'deep', accent: '#e0902f' },
  pearl: { to: 'pearl_ac', accent: '#c93a3a' }, white: { to: 'pearl_ac', accent: '#1d1d1f' },
  sage: { to: 'deep_light', accent: '#2e8b5e' }, sky: { to: 'deep_light', accent: '#2f6fd0' }, lavender: { to: 'deep_light', accent: '#7c4ddb' },
  apricot: { to: 'deep_light', accent: '#c97a1e' },
  fluent: { to: 'fluent', accent: '#60cdff' }, fluent_light: { to: 'fluent_light', accent: '#005fb8' },
  nostalgia: { to: 'nostalgia', accent: '#2fb8ec' }, nostalgia_night: { to: 'nostalgia_night', accent: '#3cc4f3' }
}
const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k)
/** A stored value made safe: a theme key (old keys give their new theme), or 'default' */
export const parseStyle = (raw: unknown): string =>
  (typeof raw !== 'string' ? DEFAULT_STYLE : BY_KEY.has(raw) ? raw : hasOwn(LEGACY_STYLES, raw) ? LEGACY_STYLES[raw].to : DEFAULT_STYLE)
/** The main colour an old stored key implies (its own theme's colour), or null */
export const impliedAccent = (raw: unknown): string | null => (typeof raw === 'string' && hasOwn(LEGACY_STYLES, raw) ? LEGACY_STYLES[raw].accent : null)

/** The value stored on this device, as it is (null: none, or blocked storage) */
export function readStyleRaw(): string | null {
  try { return localStorage.getItem(STYLE_KEY) } catch { return null }
}
/** The theme chosen on this device (old keys give their new theme; blocked storage = Default) */
export const readStyle = (): string => parseStyle(readStyleRaw())
/** Stores the choice (Default removes the key, so the device is as before any theme was picked) */
export function storeStyle(key: string): void {
  try {
    if (parseStyle(key) === DEFAULT_STYLE) localStorage.removeItem(STYLE_KEY)
    else localStorage.setItem(STYLE_KEY, key)
  } catch { /* storage full or blocked */ }
}

/** The theme shown for a choice: with "Follow my device", the one of the pair in the tab the system asks for */
export function shownStyle(key: string, follow: boolean, systemDark: boolean): string {
  const s = styleDef(key)
  if (!s || !follow) return s ? s.key : DEFAULT_STYLE
  return s.group === (systemDark ? 'dark' : 'light') ? s.key : s.twin
}

/** Every class a theme may put on <html> (removed when another theme is applied) */
export const STYLE_CLASSES = ['themed', 'theme-flat', 'theme-night', 'theme-bubbles', 'theme-fluent', 'theme-aero', 'theme-nostalgia', 'theme-fourkeys', 'theme-contrast'] as const

/** The classes of a theme on <html> (none for Default) */
export function styleClasses(key: string): string[] {
  const s = styleDef(key)
  if (!s) return []
  const c = ['themed']
  if (s.flat) c.push('theme-flat')
  if (s.family) c.push('theme-' + s.family)
  if (s.night) c.push('theme-night')
  return c
}

/** Puts a theme on <html>: data-style and the classes (Default: none of them). Light / dark is the theme store's job.
 *  Returns whether the theme is dark (null = Default: the user's own light / dark) */
export function applyStyleToHtml(html: HTMLElement, key: string): boolean | null {
  const s = styleDef(key)
  for (const c of STYLE_CLASSES) html.classList.remove(c)
  html.removeAttribute('data-from')
  if (!s) { html.removeAttribute('data-style'); return null }
  html.setAttribute('data-style', s.key)
  for (const c of styleClasses(s.key)) html.classList.add(c)
  return s.dark
}

// ===== Colour tokens =====
const trip = (c: Rgb) => c.join(' ')
/** fg seen over bg with opacity a */
const over = (fg: string, bg: string, a = 1): Rgb => mixc(hex2rgb(bg), hex2rgb(fg), a)

/** A theme's palette for a main colour (fixed themes: their own; no colour: the reference one) */
export function resolvedPalette(key: string, accent?: string | null): Palette | undefined {
  const s = styleDef(key)
  if (!s) return undefined
  return s.recipe && accent && /^#[0-9a-f]{6}$/i.test(accent) ? buildPalette(s.recipe, accent, s.dark) : s.palette
}

/** Every token a theme sets, in order (the theme store removes them all when going back to Default) */
export const TOKEN_NAMES = ['--bg', '--card', '--inp', '--btn', '--thumb', '--g100', '--g200', '--g300', '--g400', '--g500', '--g600', '--g800', '--r900', '--r950', '--gr4',
  '--sb', '--brand', '--brand-dark', '--on-brand', '--brand-text', '--brand-light', '--btn-border', '--sphere', '--danger', '--card-a', '--inp-a', '--sky-a', '--sky-b'] as const

/** Hideo's design tokens of a theme for a main colour (adaptive themes; the others ignore it), in TOKEN_NAMES order ("r g b"
 *  values; --sb, --sky-a, --sky-b are colours, --card-a and --inp-a numbers). Empty for Default */
export function styleTokens(key: string, accentHex?: string | null): [string, string][] {
  const s = styleDef(key)
  const p = resolvedPalette(key, accentHex)
  if (!s || !p) return []
  const bg = hex2rgb(p.bg), text = hex2rgb(p.text), gray = hex2rgb(p.gray), grayDark = hex2rgb(p.grayDark)
  // Panels and fields as they look on the page (see-through ones over the page background)
  const card = over(p.card, p.bg, p.cardA ?? 1), inp = over(p.inp, p.bg, p.inpA ?? 1)
  const accent = accentVars(p.accent, s.dark, { bgs: [bg, card, inp], base: card })
  const get = (k: string) => accent.find(([n]) => n === k)![1]
  return [
    ['--bg', trip(bg)], ['--card', trip(hex2rgb(p.card))], ['--inp', trip(hex2rgb(p.inp))], ['--btn', trip(hex2rgb(p.btn))],
    ['--thumb', get('--thumb')],
    ['--g100', trip(text)], ['--g200', trip(mixc(text, gray, 0.25))], ['--g300', trip(mixc(text, gray, 0.55))], ['--g400', trip(gray)],
    ['--g500', trip(grayDark)], ['--g600', trip(mixc(grayDark, bg, 0.35))], ['--g800', trip(hex2rgb(p.border))],
    ['--r900', get('--r900')], ['--r950', get('--r950')], ['--gr4', trip(hex2rgb(p.green))], ['--sb', p.btnBorder],
    ['--brand', get('--brand')], ['--brand-dark', get('--brand-dark')], ['--on-brand', get('--on-brand')], ['--brand-text', get('--brand-text')],
    ['--brand-light', trip(hex2rgb(p.accentLight))], ['--btn-border', trip(hex2rgb(p.btnBorder))], ['--sphere', trip(hex2rgb(p.sphere))],
    ['--danger', trip(hex2rgb(p.red))], ['--card-a', String(p.cardA ?? 1)], ['--inp-a', String(p.inpA ?? 1)],
    ['--sky-a', p.skyA], ['--sky-b', p.skyB]
  ]
}
/** The tokens as inline CSS text ("--bg: 1 2 3; …") */
export const tokenCss = (tokens: [string, string][]): string => tokens.map(([k, v]) => `${k}: ${v};`).join(' ')

/** Hideo's own token values (legacy.css, html.dark / html:not(.dark)) and the main colour: the Default preview */
export function defaultTokens(dark: boolean, accent: string): [string, string][] {
  const base: [string, string][] = dark
    ? [['--bg', '15 15 15'], ['--card', '26 26 26'], ['--inp', '38 38 38'], ['--btn', '42 42 42'], ['--g100', '243 244 246'], ['--g400', '156 163 175'], ['--g800', '31 41 55']]
    : [['--bg', '244 244 242'], ['--card', '255 255 255'], ['--inp', '243 242 240'], ['--btn', '235 233 230'], ['--g100', '24 24 27'], ['--g400', '113 113 122'], ['--g800', '228 228 231']]
  const bg = 'rgb(' + base[0][1].split(' ').join(',') + ')'
  return [...base, ...accentVars(accent, dark), ['--card-a', '1'], ['--sky-a', bg], ['--sky-b', bg]]
}

/** The old keys whose own colour differs from their new theme's reference colour: the stylesheet has a block for each
 *  (html[data-from="key"]), used by the anti-flash script until the theme store has run once */
const fromBlocks = (): [string, StyleDef, string][] =>
  Object.entries(LEGACY_STYLES).filter(([, v]) => v.accent !== styleDef(v.to)!.palette.accent).map(([k, v]) => [k, styleDef(v.to)!, v.accent])

/** The stylesheet with every theme's tokens (src/styles/theme-tokens.css is this text; npm test checks it) */
export function styleTokenCss(): string {
  const head = '/* Hideo - colour tokens of the app themes. Written by styleTokenCss() in src/features/appThemes.ts: do not edit by hand\n' +
    '   (WRITE_THEME_TOKENS=1 npx vitest run tests/appThemes.test.ts writes it again). Default has no block: Hideo as always.\n' +
    '   The theme store writes the tokens of the main colour on <html> (they win over these); these blocks (each theme with its\n' +
    '   reference colour, and the old keys with their own colour) only cover the first moments before it runs. */\n'
  const line = (sel: string, s: StyleDef, accent?: string) => `${sel} { ${tokenCss(styleTokens(s.key, accent))} color-scheme: ${s.dark ? 'dark' : 'light'}; }`
  return head + [...STYLES.map(s => line(`html[data-style="${s.key}"]`, s)),
    ...fromBlocks().map(([k, s, a]) => line(`html[data-style][data-from="${k}"]`, s, a))].join('\n') + '\n'
}

/** The anti-flash script's table (index.html): every valid stored key → "shown key|classes on <html>|old key|group and
 *  twin" (the old key only for the old keys with a stylesheet block of their own, else empty; the last part is the shown
 *  theme's group, d or l, then its twin's key, used with "Follow my device" when the store's cache is missing).
 *  tests/appThemes.test.ts checks index.html has it */
export function antiFlashTable(): string {
  const from = new Set(fromBlocks().map(([k]) => k))
  const entry = (stored: string, key: string) => {
    const s = styleDef(key)!
    return `${stored}:'${key}|${[...styleClasses(key), ...(s.dark ? ['dark'] : [])].join(' ')}|${from.has(stored) ? stored : ''}|${s.group === 'dark' ? 'd' : 'l'}${s.twin}'`
  }
  return '{' + [...STYLES.map(s => entry(s.key, s.key)), ...Object.entries(LEGACY_STYLES).filter(([k]) => !BY_KEY.has(k)).map(([k, v]) => entry(k, v.to))].join(',') + '}'
}

/** Inline style of a picker preview: the theme's tokens with the main colour on the bubble (Default: Hideo's own light or
 *  dark, as asked, with the colour) */
export function previewStyle(key: string, dark: boolean, accent: string): Record<string, string> {
  return Object.fromEntries(styleDef(key) ? styleTokens(key, accent) : defaultTokens(dark, accent))
}
